import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Search, Star, MapPin, Briefcase, Clock, Award, ChevronRight,
  Filter, Users, Loader2, Shield, TrendingUp, Phone
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_BADGE = {
  gold: { bg: "bg-amber-100 text-amber-700 border-amber-300", label: "Gold Partner" },
  silver: { bg: "bg-gray-100 text-gray-600 border-gray-300", label: "Silver Partner" },
  bronze: { bg: "bg-orange-100 text-orange-700 border-orange-300", label: "Bronze" },
  flagged: { bg: "bg-red-100 text-red-600 border-red-300", label: "Under Review" },
};

const VendorCard = ({ vendor }) => {
  const qs = vendor.quality_score || 50;
  const tier = qs >= 80 ? "gold" : qs >= 60 ? "silver" : qs >= 40 ? "bronze" : "flagged";
  const badge = TIER_BADGE[tier];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-lg hover:border-[#E83E8C]/20 transition-all group" data-testid={`vendor-card-${vendor.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-base font-bold text-[#1E1B4B] group-hover:text-[#E83E8C] transition-colors">{vendor.name}</h3>
          <p className="text-xs text-[#94a3b8] flex items-center gap-1 mt-0.5">
            <Briefcase size={10} /> {vendor.service_type}
          </p>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${badge.bg}`}>{badge.label}</span>
      </div>

      {vendor.about && <p className="text-xs text-[#475569] line-clamp-2 mb-3">{vendor.about}</p>}

      <div className="flex flex-wrap gap-1.5 mb-3">
        {(vendor.service_area || []).slice(0, 3).map((area) => (
          <span key={`area-${area}`} className="px-2 py-0.5 rounded-full text-[9px] bg-indigo-50 text-indigo-600 font-medium flex items-center gap-1">
            <MapPin size={8} /> {area}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-50">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Star size={12} className="text-amber-400 fill-amber-400" />
            <span className="text-xs font-bold text-[#1E1B4B]">{vendor.rating || "New"}</span>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-[#94a3b8]">
            <Award size={10} /> QS: {vendor.quality_score || 50}
          </div>
          <div className="flex items-center gap-1 text-[10px] text-[#94a3b8]">
            <Clock size={10} /> {vendor.experience_years || 0}yr
          </div>
        </div>
        {vendor.pricing_range && (
          <span className="text-xs font-bold text-[#E83E8C]">{vendor.pricing_range}</span>
        )}
      </div>
    </div>
  );
};

export default function VendorMarketplace() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serviceType, setServiceType] = useState("");
  const [area, setArea] = useState("");
  const [sortBy, setSortBy] = useState("quality_score");
  const [serviceTypes, setServiceTypes] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/vendors/service-types`)
      .then(r => r.json())
      .then(d => {
        setServiceTypes(d.service_types || []);
        setServiceAreas(d.service_areas || []);
      }).catch(err => console.error("Vendor service-types fetch:", err));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (serviceType) params.set("service_type", serviceType);
    if (area) params.set("area", area);
    params.set("sort_by", sortBy);

    fetch(`${API_URL}/api/vendors/marketplace?${params}`)
      .then(r => r.json())
      .then(d => setVendors(d.vendors || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [serviceType, area, sortBy]);

  return (
    <div className="min-h-screen bg-[#FFFAF5]" data-testid="vendor-marketplace">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1E1B4B] via-indigo-900 to-[#E83E8C]/30 py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <Link to="/" className="text-white/50 text-xs hover:text-white mb-4 inline-block">&larr; Back to DoozyGo</Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 font-['Outfit']">DoozyGo Vendor Marketplace</h1>
          <p className="text-white/60 text-sm max-w-lg mx-auto">Verified event professionals. Quality guaranteed. Book the best decorators, caterers, photographers, and more.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 -mt-8">
        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 mb-8 flex flex-wrap gap-3 items-center" data-testid="marketplace-filters">
          <Filter size={16} className="text-[#94a3b8]" />
          <select value={serviceType} onChange={e => setServiceType(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-xs bg-white" data-testid="filter-service-type">
            <option value="">All Services</option>
            {serviceTypes.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={area} onChange={e => setArea(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-xs bg-white" data-testid="filter-area">
            <option value="">All Areas</option>
            {serviceAreas.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-xs bg-white" data-testid="filter-sort">
            <option value="quality_score">Highest Quality</option>
            <option value="rating">Best Rated</option>
            <option value="total_jobs">Most Experienced</option>
          </select>
          <span className="ml-auto text-xs text-[#94a3b8]">{vendors.length} vendor{vendors.length !== 1 ? "s" : ""}</span>
        </div>

        {/* Vendor Grid */}
        {loading ? (
          <div className="text-center py-16"><Loader2 className="animate-spin text-[#E83E8C] mx-auto" size={32} /></div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <Users size={40} className="text-[#E83E8C]/30 mx-auto mb-3" />
            <p className="text-sm font-medium text-[#1E1B4B]">No vendors listed yet</p>
            <p className="text-xs text-[#94a3b8] mt-1">Vendors are being onboarded. Check back soon!</p>
            <Link to="/vendor-register" className="inline-block mt-4 px-6 py-2.5 rounded-xl bg-[#E83E8C] text-white text-xs font-medium hover:bg-[#d1356f] transition-colors" data-testid="register-vendor-link">
              Become a Vendor
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendors.map(v => <VendorCard key={v.id} vendor={v} />)}
          </div>
        )}

        {/* CTA */}
        <div className="text-center py-12">
          <p className="text-xs text-[#94a3b8] mb-2">Are you an event professional?</p>
          <Link to="/vendor-register" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#1E1B4B] text-white text-sm font-medium hover:bg-[#2d2a5e] transition-colors" data-testid="join-marketplace-btn">
            <Briefcase size={14} /> Join the DoozyGo Network <ChevronRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
